/**
 * @notes: API Service. This is a wrapper around axios and will make api calls to API. This is convenient as it allows
 * the application to interchange 3rd party libraries or even use a custom one, if the need arises, without need to change
 * all over codebase.
 */
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  CancelTokenSource,
} from "axios";
import { getEnhancedAxiosErrorStack } from "log/getEnhancedAxiosErrorStack";

import {
  LOGIN_ENDPOINT,
  LOGOUT_ENDPOINT,
  RECOVER_PASSWORD_ENDPOINT,
  SIGNUP_ENDPOINT,
  SSO_LOGIN_ENDPOINT,
} from "containers/authentication/redux/endpoints";

import { API_BASE_URL } from "./endpoints";
import enhanceAxiosStack from "./enhanceAxiosErrorStack";
import logClientIdMissing from "./logClientIdMissing";
import {
  prepareQueryWithArrays,
  reComputeError,
  shouldRunInBackground,
} from "./utils";

const isAuthEndpoint = (url?: string) => {
  if (!url) return false;
  return (
    url?.includes(LOGIN_ENDPOINT) ||
    url?.includes(SSO_LOGIN_ENDPOINT) ||
    url?.includes(SIGNUP_ENDPOINT) ||
    url?.includes(LOGOUT_ENDPOINT) ||
    url?.includes(RECOVER_PASSWORD_ENDPOINT)
  );
};

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  detailedActionName?: string;
}
class ApiService {
  // fields
  cancelToken: CancelTokenSource & { url?: string };
  service: AxiosInstance;
  token: string;
  clientId: string;
  runInBackground: boolean;
  loggingOut: boolean;

  constructor() {
    this.cancelToken = axios.CancelToken.source();
    this.service = axios.create({
      baseURL: API_BASE_URL(),
      headers: {
        "Content-Type": "multipart/form-data",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "*",
        "Access-Control-Allow-Methods": "PUT, GET, POST, DELETE",
      },
      timeout: 180000,
    });
    this.token = "";
    this.clientId = "";
    this.runInBackground = false;
    this.loggingOut = false;
    this.service.interceptors.request.use(this.handleRequestInterceptor);
    this.service.interceptors.response.use(
      this.handleResponseSuccess,
      this.handleResponseError,
    );

    enhanceAxiosStack(this.service);
  }

  setToken(token: string) {
    this.token = token;
  }

  tokenPreview = (token: string) => {
    return (token || "").slice(0, 12) + (token ? "..." : "");
  };

  setClientId(clientId: string) {
    this.clientId = clientId;
  }

  setRunInBackground(runInBackground: boolean) {
    this.runInBackground = runInBackground;
  }

  setLoggingOut(loggingOut: boolean) {
    this.loggingOut = loggingOut;
  }

  /**
   * Request interceptor config. This is called before any request is made to API.
   * @param {Object} config
   * @return {Object} config object for axios
   * */
  handleRequestInterceptor = (config: any) => {
    const isLogoutUrl = config.url === LOGOUT_ENDPOINT;

    if (this.loggingOut && !isLogoutUrl) {
      return Promise.reject(
        new axios.Cancel("Request blocked: logout in progress"),
      );
    }
    config.headers = {
      Auth: this.token,
    };

    if (this.clientId) {
      config.headers["client-id"] = this.clientId;
      config.headers["local-timestamp"] = new Date().toISOString();
    } else if (this.token) {
      // User is authenticated but clientId is missing — this is a real bug worth logging.
      logClientIdMissing(config);
    } else if (!isAuthEndpoint(config.url)) {
      // User is logged out (no token, no clientId) and this is not an auth endpoint.
      // In-flight requests from a concurrent Promise.all (e.g. fetchTeams, fetchMetaExpressions)
      // that outlive the logout should be cancelled here to prevent them from reaching
      // the backend and generating spurious "requires a valid client-id header" errors.
      throw new axios.Cancel(
        `Request to ${config.url} cancelled: user is not authenticated`,
      );
    }
    if (config?.detailedActionName) {
      config.headers["detailed-action-code"] = config?.detailedActionName;
      config.headers["local-timestamp"] = new Date().toISOString();
    }
    if (this.runInBackground) {
      config.headers["run-in-background"] = "true";
      config.headers["local-timestamp"] = new Date().toISOString();
    }
    this.setRunInBackground(false);
    return config;
  };

  /**
   * Handles successful Response. This is used to handle the response accordingly
   * MUST return the response
   * @param {Object} response Response Object from request
   * @return {Object} Response*/
  // Reference https://github.com/axios/axios#response-schema
  handleResponseSuccess = (response: any) => {
    // extract the data block received from Smala API
    // this will contain the data we need for the application
    const { data } = response.data;

    return {
      ...response,
      data,
    };
  };

  /**
   * Handles response errors
   * @param {Object} error Error Object received from response
   * */
  // Reference https://github.com/axios/axios#handling-errors
  handleResponseError = (initialError: any) => {
    if (axios.isCancel(initialError)) {
      // For cancelled requests, we don't want to retry or enhance the stack, just reject with the original error.
      return Promise.reject(initialError);
    }

    const error = reComputeError(initialError);

    const config = error.config;

    const enhancedStack = getEnhancedAxiosErrorStack(error);

    function removeMatchingStacks(
      stack: string,
      excludePatterns: string[] = ["node_modules"],
    ) {
      return stack
        ?.split("\n")
        .filter(
          (line) => !excludePatterns.some((pattern) => line.includes(pattern)),
        )
        .join("\n");
    }

    if (error) {
      error.stack = removeMatchingStacks(enhancedStack ?? error.stack ?? "", [
        "node_modules",
        "enhanceAxiosErrorStack",
        "apiService",
      ]);
    }

    // If config does not exist or the retry option is not set, reject
    if (!config || !config.retry) return Promise.reject(error);

    // Set the variable for keeping track of the retry count
    config.__retryCount = config.__retryCount || 0;

    // Check if we've maxed out the total number of retries
    if (config.__retryCount >= config.retry) {
      // Reject with the error
      return Promise.reject(error);
    }

    // Increase the retry count
    config.__retryCount += 1;

    // Create new promise to handle exponential backoff
    const backoff = new Promise<void>(function (resolve) {
      setTimeout(function () {
        resolve();
      }, config.retryDelay || 1);
    });

    // Return the promise in which recalls axios to retry the request
    return backoff.then(function () {
      return axios(config);
    });
  };

  redirectTo(path: Location) {
    document.location = path.toString();
  }

  multipartUpdate = (
    endpoint: string,
    files: any[],
    body: any = {},
    detailedActionName = "UNKNOWN",
  ) => {
    const formData = new FormData();
    files.forEach((f: any) => {
      const { file, fileName, value } = f;
      formData.append(fileName, file ?? value, fileName);
    });
    for (const att in body) {
      const attrType = typeof body[att];
      formData.set(
        att,
        attrType === "object" ? JSON.stringify(body[att]) : body[att],
      );
    }
    this.cancelToken = axios.CancelToken.source();
    const config: ExtendedAxiosRequestConfig = {
      headers: {
        "content-type": "multipart/form-data",
      },
      cancelToken: this.cancelToken.token,
      detailedActionName,
    };
    return this.service.post(endpoint, formData, config);
  };

  /**
   * Perform POST requests to API
   * @param {String} url Url to make POST request
   * @param {Object} payload Payload Object data to POST to api
   * */
  post(
    url: string,
    payload?: any,
    detailedActionName = "UNKNOWN" as string,
    cancelToken?: CancelTokenSource["token"],
    config?: ExtendedAxiosRequestConfig,
    runInBackground?: boolean,
  ) {
    if (shouldRunInBackground(payload) || runInBackground) {
      this.setRunInBackground(true);
    }
    return this.service.post(url, payload, {
      detailedActionName,
      ...(cancelToken && { cancelToken }),
      ...(config || {}),
    } as ExtendedAxiosRequestConfig);
  }

  /**
   * perform GET request
   * @param {String} url
   * */
  get(
    url: string,
    query = {} as any,
    cancelToken?: CancelTokenSource["token"],
    runInBackground?: boolean,
  ) {
    if (runInBackground) {
      this.setRunInBackground(true);
    }
    return this.service.get(url, {
      params: query,
      paramsSerializer: (query: any) => prepareQueryWithArrays(query),
      cancelToken,
    });
  }

  /**
   * Patch HTTP verb,handles patch requests
   * @param {String} url
   * @param {Object} payload
   * */
  patch(
    url: string,
    payload?: any,
    detailedActionName = "UNKNOWN" as string,
    cancelToken?: CancelTokenSource["token"],
    config?: ExtendedAxiosRequestConfig,
    runInBackground?: boolean,
  ) {
    if (runInBackground || shouldRunInBackground(payload)) {
      this.setRunInBackground(true);
    }
    return this.service.patch(url, payload, {
      detailedActionName,
      ...(cancelToken && { cancelToken }),
      ...(config || {}),
    } as ExtendedAxiosRequestConfig);
  }

  /**
   * Delete HTTP verb, handles delete requests
   * @param {String} url Url to post the delete method to
   * @param {Object} payload [OPTIONAL] Payload to send with request
   */
  delete(
    url: string,
    payload = {} as any,
    detailedActionName = "UNKNOWN",
    cancelToken?: CancelTokenSource["token"],
    config?: ExtendedAxiosRequestConfig,
    runInBackground?: boolean,
  ) {
    if (runInBackground || shouldRunInBackground(payload)) {
      this.setRunInBackground(true);
    }
    return this.service.delete(url, {
      data: {
        ...payload,
      },
      detailedActionName,
      ...(cancelToken && { cancelToken }),
      ...(config || {}),
    } as ExtendedAxiosRequestConfig);
  }
}

export default new ApiService();
